import type { AxiosError } from "axios";
import axios from "../../../axios";
import { NonDeliverable, StatementOfTaxability } from "../../../shared/constants";
import { buildApiV3Url } from "../../../shared/Utils";

export type ExemptionsDetailsRow = Record<string, unknown>;

export interface ExposureZonePrefill {
    id: string | number;
    name: string;
    isSST?: unknown;
    initials?: unknown;
}

export interface ExemptionOverrideEditFormValues {
    exposureZone: ExposureZonePrefill[];
    taxCodeId: string;
    licenseNumber: string;
    note: string;
    effectiveDate: string;
    expirationDate: string;
    doesntExpire: boolean;
}

export interface PostExemptionOverridePayload {
    exposureZoneId: number;
    taxCodeId: number;
    licenseNumber?: string | null;
    note?: string;
    effectiveDate: string;
    expirationDate: string;
}

/**
 * Display label for exemption override reason dropdown.
 * TAXABLE → StatementOfTaxability; NON-DELIVERABLE → NonDeliverable; else sentence-style casing.
 */
/**
 * True when the row has a manual tax code / exemption override record id (authoritative override indicator).
 */
export function hasManualTaxCodeChangesOverride(row: unknown): boolean {
    if (!row || typeof row !== "object") {
        return false;
    }
    const r = row as ExemptionsDetailsRow;
    const id = r.manualTaxCodeChangesId ?? r.ManualTaxCodeChangesId;
    return id !== null && id !== undefined && id !== "";
}

/**
 * True when the exemptions-details row represents a manual exemption override (not a standard certificate row).
 * Supports common API shapes (camelCase and PascalCase).
 */
export function isExemptionOverrideGridRow(row: unknown): boolean {
    if (!row || typeof row !== "object") {
        return false;
    }
    const r = row as ExemptionsDetailsRow;
    if (hasManualTaxCodeChangesOverride(r)) {
        return true;
    }
    if (r.isExemptionOverride === true || r.IsExemptionOverride === true) {
        return true;
    }
    if (r.hasExemptionOverride === true || r.HasExemptionOverride === true) {
        return true;
    }
    const st = String(r.certificateStatus ?? r.CertificateStatus ?? "")
        .trim()
        .toUpperCase();
    if (st === "OVERRIDDEN" || st === "OVERRIDE") {
        return true;
    }
    if (r.exemptionOverrideId != null || r.ExemptionOverrideId != null) {
        return true;
    }
    return false;
}

/**
 * Picks the first defined, non-empty string value (for date fields from mixed API shapes).
 */
function firstNonEmptyDate(...values: unknown[]): string | undefined {
    for (const v of values) {
        if (v !== undefined && v !== null && v !== "") {
            return typeof v === "string" ? v : String(v);
        }
    }
    return undefined;
}

/**
 * Maps exemptions-details rows to the certificate grid date fields (`signedDate`, `expirationDate`).
 * Exemption override rows often expose `effectiveDate` / `EffectiveDate` instead of `signedDate`, and
 * may use PascalCase for expiration.
 */
export function normalizeExemptionsDetailsRow(row: unknown): ExemptionsDetailsRow {
    if (!row || typeof row !== "object") {
        return row as ExemptionsDetailsRow;
    }
    const next = { ...(row as ExemptionsDetailsRow) };
    const signed = firstNonEmptyDate(
        next.signedDate,
        next.SignedDate,
        next.effectiveDate,
        next.EffectiveDate
    );
    if (signed !== undefined) {
        next.signedDate = signed;
    }
    const expiration = firstNonEmptyDate(
        next.expirationDate,
        next.ExpirationDate,
        next.expireDate,
        next.ExpireDate
    );
    if (expiration !== undefined) {
        next.expirationDate = expiration;
    }
    return next;
}

/**
 * Builds SelectExposureZone value from an exemptions-details grid row (prefill region when adding an override from that row).
 */
export function getExposureZonePrefillFromExemptionsDetailsRow(
    row: unknown
): ExposureZonePrefill | null {
    if (!row || typeof row !== "object") {
        return null;
    }
    const r = row as ExemptionsDetailsRow;
    const id = r.exposureZoneId ?? r.ExposureZoneId;
    if (id === null || id === undefined || id === "") {
        return null;
    }
    const nameRaw = r.exposureZoneName ?? r.ExposureZoneName;
    return {
        id,
        name: String(nameRaw ?? "").trim() || String(id),
        isSST: r.isSST ?? r.IsSST,
        initials: r.initials ?? r.Initials,
    };
}

const DEFAULT_OVERRIDE_EXPIRATION = "9999-12-31";

/**
 * True when expiration represents open-ended / "doesn't expire" (ISO 9999-12-31 or US 12/31/9999).
 */
export function isNeverExpireOverrideExpiration(raw: unknown): boolean {
    if (raw === null || raw === undefined || raw === "") {
        return false;
    }
    const s = String(raw).trim();
    const iso10 = s.length >= 10 ? s.substring(0, 10) : s;
    if (iso10 === DEFAULT_OVERRIDE_EXPIRATION || iso10.startsWith("9999")) {
        return true;
    }
    const us = /^(\d{1,2})\/(\d{1,2})\/(9999)(?:\b|T|\s|$)/.exec(s);
    if (us) {
        const month = parseInt(us[1], 10);
        const day = parseInt(us[2], 10);
        return month === 12 && day === 31;
    }
    return false;
}

/** Normalize expiration to `YYYY-MM-DD` for Skylab date inputs when not open-ended. */
function expirationValueForDateInput(expRaw: unknown): string {
    const s = String(expRaw ?? "").trim();
    if (isNeverExpireOverrideExpiration(s)) {
        return DEFAULT_OVERRIDE_EXPIRATION;
    }
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (iso) {
        return `${iso[1]}-${iso[2]}-${iso[3]}`;
    }
    const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);
    if (us) {
        const month = parseInt(us[1], 10);
        const day = parseInt(us[2], 10);
        const year = parseInt(us[3], 10);
        const pad = (n: number) => String(n).padStart(2, "0");
        return `${year}-${pad(month)}-${pad(day)}`;
    }
    return s.length >= 10 ? s.substring(0, 10) : s;
}

/**
 * API record id for PUT .../exemption-overrides/{id} — prefers manual tax code change id, then exemption override id.
 */
export function getExemptionOverrideRecordId(row: unknown): number | null {
    if (!row || typeof row !== "object") {
        return null;
    }
    const r = row as ExemptionsDetailsRow;
    const manual = r.manualTaxCodeChangesId ?? r.ManualTaxCodeChangesId;
    if (manual !== null && manual !== undefined && manual !== "") {
        const n = Number(manual);
        return Number.isFinite(n) ? n : null;
    }
    const ex = r.exemptionOverrideId ?? r.ExemptionOverrideId;
    if (ex !== null && ex !== undefined && ex !== "") {
        const n = Number(ex);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}

/**
 * Maps an exemptions-details override row into the exemption override modal (all persisted fields).
 */
export function buildExemptionOverrideEditFormValues(
    row: ExemptionsDetailsRow
): ExemptionOverrideEditFormValues {
    const r = normalizeExemptionsDetailsRow({ ...row });
    const zone = getExposureZonePrefillFromExemptionsDetailsRow(r);
    const exposureZone = zone ? [zone] : [];
    const tid =
        r.actualTaxCodeId ??
        r.ActualTaxCodeId ??
        r.taxCodeId ??
        r.TaxCodeId ??
        r.expectedTaxCodeId ??
        r.ExpectedTaxCodeId;
    const taxCodeId = tid !== null && tid !== undefined && tid !== "" ? String(tid) : "";
    const licenseNumber = String(
        r.licenseNumber ??
            r.LicenseNumber ??
            r.license ??
            r.License ??
            r.licenseId ??
            r.LicenseId ??
            ""
    ).trim();
    const note = String(
        r.note ??
            r.Note ??
            r.notes ??
            r.Notes ??
            r.exemptionOverrideNote ??
            r.ExemptionOverrideNote ??
            r.manualTaxCodeNote ??
            r.ManualTaxCodeNote ??
            r.overrideNote ??
            r.OverrideNote ??
            r.comments ??
            r.Comments ??
            ""
    ).trim();
    const signedRaw = r.signedDate;
    const effectiveDate = signedRaw ? String(signedRaw).substring(0, 10) : "";
    const expRaw = r.expirationDate;
    let doesntExpire = true;
    let expirationDate = DEFAULT_OVERRIDE_EXPIRATION;
    if (expRaw) {
        if (isNeverExpireOverrideExpiration(expRaw)) {
            doesntExpire = true;
            expirationDate = DEFAULT_OVERRIDE_EXPIRATION;
        } else {
            doesntExpire = false;
            expirationDate = expirationValueForDateInput(expRaw);
        }
    }
    return {
        exposureZone,
        taxCodeId,
        licenseNumber,
        note,
        effectiveDate,
        expirationDate,
        doesntExpire,
    };
}

export function formatExemptionOverrideReasonLabel(rawName: string | undefined | null): string {
    const name = String(rawName ?? "").trim();
    if (!name) return "";
    const normalized = name.toUpperCase().replace(/-/g, " ").replace(/\s+/g, " ").trim();
    if (normalized === "TAXABLE") {
        return StatementOfTaxability;
    }
    if (normalized === "NON DELIVERABLE") {
        return NonDeliverable;
    }
    const lower = name.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Fetches tax codes eligible for exemption overrides — i.e. exemption reasons
 * flagged as `markActual`. Uses the company-scoped `exemption-reasons` route
 * (which the global axios request interceptor automatically prefixes with
 * `companies/{activeCompanyId}/`). A second URL is tried as a fallback in case
 * the OData filter is rejected by the server.
 */
export async function fetchExemptionOverrideTaxCodes(): Promise<
    Array<{ id: number; name: string }>
> {
    const tryUrls = [
        buildApiV3Url(
            "exemption-reasons?$filter=markActual eq true&$select=id,name&$orderBy=name ASC&$top=500"
        ),
        buildApiV3Url("exemption-reasons?$select=id,name,markActual&$top=500"),
    ];
    let lastError: unknown;
    for (const url of tryUrls) {
        try {
            const res = await axios.get(url, { withCredentials: true });
            const body: unknown = res?.data;
            const raw: unknown =
                body !== null &&
                typeof body === "object" &&
                "value" in body &&
                Array.isArray((body as { value: unknown }).value)
                    ? (body as { value: unknown[] }).value
                    : Array.isArray(body)
                      ? body
                      : [];
            const list = raw as Array<Record<string, unknown>>;
            return list
                .filter(row => {
                    if (!row || (row.id === undefined && row.Id === undefined)) {
                        return false;
                    }
                    // If `markActual` is present (fallback URL doesn't filter
                    // server-side), keep only rows where it is truthy.
                    const markActual = row.markActual ?? row.MarkActual;
                    if (markActual === undefined) {
                        return true;
                    }
                    return Boolean(markActual);
                })
                .map(row => ({
                    id: Number(row.id ?? row.Id),
                    name: String(row.name ?? row.Name ?? ""),
                }));
        } catch (e) {
            lastError = e;
        }
    }
    throw lastError;
}

/**
 * Creates a customer exemption override.
 * Uses `POST .../customers/{id}/exemption-overrides` (replaces legacy
 * `.../customer-exemption-override`) for access control and the current API controller.
 */
export function postCustomerExemptionOverride(
    customerId: number,
    payload: PostExemptionOverridePayload
) {
    return axios.post(buildApiV3Url(`customers/${customerId}/exemption-overrides`), payload, {
        withCredentials: true,
    });
}

/**
 * Reads the active flag from GET …/exemption-overrides list/detail when the API provides it.
 * `undefined` when the payload has no recognized active field.
 */
function getExemptionOverrideApiActiveIndicator(detail: unknown): boolean | undefined {
    if (!detail || typeof detail !== "object") {
        return undefined;
    }
    const d = detail as ExemptionsDetailsRow;
    const keys = [
        "isActive",
        "IsActive",
        "active",
        "Active",
        "isActiveIndicator",
        "IsActiveIndicator",
        "activeIndicator",
        "ActiveIndicator",
    ];
    for (const k of keys) {
        if (!Object.prototype.hasOwnProperty.call(d, k)) {
            continue;
        }
        const v = d[k];
        if (v === undefined || v === null) {
            continue;
        }
        if (typeof v === "boolean") {
            return v;
        }
        if (typeof v === "string") {
            const s = v.trim().toLowerCase();
            if (s === "true") {
                return true;
            }
            if (s === "false") {
                return false;
            }
        }
        if (typeof v === "number" && Number.isFinite(v)) {
            return v !== 0;
        }
    }
    return undefined;
}

/**
 * True if override expiration is unset, open-ended (9999), or on/after today (active for display filtering).
 */
export function isExemptionOverrideLikelyActive(row: unknown): boolean {
    const r = normalizeExemptionsDetailsRow({ ...(row as ExemptionsDetailsRow) });
    const exp = r.expirationDate;
    if (exp === undefined || exp === null || exp === "") {
        return true;
    }
    const s = String(exp).substring(0, 10);
    if (s.startsWith("9999")) {
        return true;
    }
    const end = new Date(`${s}T12:00:00`);
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return end >= startOfToday;
}

/**
 * Whether an override appears in the manage modal’s default list (active only).
 * Uses {@link getExemptionOverrideApiActiveIndicator} when mapped from GET …/exemption-overrides; otherwise
 * falls back to {@link isExemptionOverrideLikelyActive} (exemptions-details grid rows).
 */
export function isExemptionOverrideInDefaultModalList(row: unknown): boolean {
    if (!row || typeof row !== "object") {
        return false;
    }
    const r = row as ExemptionsDetailsRow;
    if (Object.prototype.hasOwnProperty.call(r, "exemptionOverrideIsActive")) {
        return Boolean(r.exemptionOverrideIsActive);
    }
    if (Object.prototype.hasOwnProperty.call(r, "ExemptionOverrideIsActive")) {
        return Boolean(r.ExemptionOverrideIsActive);
    }
    return isExemptionOverrideLikelyActive(row);
}

/**
 * Region display name from list/detail API (`region.name`) or exemptions-details flat fields.
 */
function getExemptionOverrideRegionDisplayName(row: ExemptionsDetailsRow): string {
    const reg = row.region ?? row.Region;
    if (reg && typeof reg === "object") {
        const n = (reg as Record<string, unknown>).name ?? (reg as Record<string, unknown>).Name;
        if (n !== null && n !== undefined && String(n).trim() !== "") {
            return String(n).trim();
        }
    }
    return String(row.exposureZoneName ?? row.ExposureZoneName ?? "").trim();
}

/**
 * Exempt reason label (`exemptReason.name`) when API returns nested objects; else grid tax code names.
 */
function getExemptionOverrideExemptReasonRawName(row: ExemptionsDetailsRow): string {
    const er = row.exemptReason ?? row.ExemptReason;
    if (er && typeof er === "object") {
        const n = (er as Record<string, unknown>).name ?? (er as Record<string, unknown>).Name;
        if (n !== null && n !== undefined && String(n).trim() !== "") {
            return String(n).trim();
        }
    }
    return "";
}

export interface ExemptionOverrideCardSummary {
    regionName: string;
    overrideName: string;
    note: string;
    effectiveDisplay: string;
    expirationDisplay: string;
}

/**
 * Label/value strings for manage-modal cards.
 */
export function getExemptionOverrideCardSummary(row: unknown): ExemptionOverrideCardSummary {
    const r = normalizeExemptionsDetailsRow({ ...(row as ExemptionsDetailsRow) });
    const regionName = getExemptionOverrideRegionDisplayName(r) || "—";
    const fromExemptReason = getExemptionOverrideExemptReasonRawName(r);
    const expected = r.expectedTaxCodeName ?? r.ExpectedTaxCodeName;
    const actual = r.actualTaxCodeName ?? r.ActualTaxCodeName;
    const rawReason = String(fromExemptReason || expected || actual || "").trim();
    const overrideName = rawReason ? formatExemptionOverrideReasonLabel(rawReason) : "—";
    const note = String(
        r.note ??
            r.Note ??
            r.exemptionOverrideNote ??
            r.ExemptionOverrideNote ??
            r.manualTaxCodeNote ??
            ""
    ).trim();
    const signed = r.signedDate ?? r.effectiveDate ?? r.EffectiveDate;
    const exp = r.expirationDate ?? r.ExpirationDate;
    const fmt = (v: unknown) => {
        if (!v) return "—";
        const s = String(v).substring(0, 10);
        const d = new Date(`${s}T12:00:00`);
        return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
    };
    return {
        regionName,
        overrideName,
        note: note || "—",
        effectiveDisplay: fmt(signed),
        expirationDisplay: fmt(exp),
    };
}

/**
 * Fetches a single exemption override by id (full record including note and license — often omitted from exemptions-details rows).
 * @param recordId — {@link getExemptionOverrideRecordId}
 */
export async function fetchCustomerExemptionOverrideDetail(
    customerId: number,
    recordId: number
): Promise<ExemptionsDetailsRow> {
    const res = await axios.get(
        buildApiV3Url(`customers/${customerId}/exemption-overrides/${recordId}`),
        { withCredentials: true }
    );
    return (res?.data ?? {}) as ExemptionsDetailsRow;
}

/**
 * Maps GET …/exemption-overrides/{id} response into the same shape as exemptions-details rows for {@link buildExemptionOverrideEditFormValues}.
 */
export function mapExemptionOverrideApiDetailToGridRowShape(detail: unknown): ExemptionsDetailsRow {
    if (!detail || typeof detail !== "object") {
        return {};
    }
    const d = detail as ExemptionsDetailsRow;
    const zone = d.exposureZone ?? d.ExposureZone;
    const regionObj = d.region ?? d.Region;
    const exemptReasonObj = d.exemptReason ?? d.ExemptReason;
    const taxCodeObj = d.taxCode ?? d.TaxCode;
    const nameFromExemptReason =
        exemptReasonObj && typeof exemptReasonObj === "object"
            ? ((exemptReasonObj as Record<string, unknown>).name ??
              (exemptReasonObj as Record<string, unknown>).Name)
            : undefined;
    const nameFromNestedTaxCode =
        taxCodeObj &&
        ((taxCodeObj as Record<string, unknown>).name ??
            (taxCodeObj as Record<string, unknown>).Name ??
            (taxCodeObj as Record<string, unknown>).displayName ??
            (taxCodeObj as Record<string, unknown>).DisplayName);
    const exposureZoneId =
        d.exposureZoneId ??
        d.ExposureZoneId ??
        (regionObj as Record<string, unknown> | undefined)?.id ??
        (regionObj as Record<string, unknown> | undefined)?.Id ??
        (zone as Record<string, unknown> | undefined)?.id ??
        (zone as Record<string, unknown> | undefined)?.Id;
    const exposureZoneName =
        d.exposureZoneName ??
        d.ExposureZoneName ??
        (regionObj as Record<string, unknown> | undefined)?.name ??
        (regionObj as Record<string, unknown> | undefined)?.Name ??
        (zone as Record<string, unknown> | undefined)?.name ??
        (zone as Record<string, unknown> | undefined)?.Name;
    const apiActive = getExemptionOverrideApiActiveIndicator(d);
    const logObj = (d.log ?? d.Log) as Record<string, unknown> | undefined;
    const noteFromLog = logObj?.entry ?? logObj?.Entry;
    const idFromExemptReason =
        exemptReasonObj && typeof exemptReasonObj === "object"
            ? ((exemptReasonObj as Record<string, unknown>).id ??
              (exemptReasonObj as Record<string, unknown>).Id)
            : undefined;
    const idFromNestedTaxCode =
        taxCodeObj && typeof taxCodeObj === "object"
            ? ((taxCodeObj as Record<string, unknown>).id ??
              (taxCodeObj as Record<string, unknown>).Id)
            : undefined;
    return {
        exposureZoneId,
        exposureZoneName,
        region: regionObj ?? undefined,
        exemptReason: exemptReasonObj ?? undefined,
        expectedTaxCodeName:
            (nameFromExemptReason as string | undefined) ??
            d.expectedTaxCodeName ??
            d.ExpectedTaxCodeName ??
            (typeof d.exemptionReason === "string" ? d.exemptionReason : undefined) ??
            (typeof d.ExemptionReason === "string" ? d.ExemptionReason : undefined) ??
            d.taxCodeName ??
            d.TaxCodeName ??
            nameFromNestedTaxCode,
        actualTaxCodeName: d.actualTaxCodeName ?? d.ActualTaxCodeName,
        actualTaxCodeId:
            d.taxCodeId ??
            d.TaxCodeId ??
            d.actualTaxCodeId ??
            d.ActualTaxCodeId ??
            d.expectedTaxCodeId ??
            d.ExpectedTaxCodeId ??
            // The API nests the override reason as { exemptReason: { id } } / { taxCode: { id } }
            idFromExemptReason ??
            idFromNestedTaxCode,
        licenseNumber: d.licenseNumber ?? d.LicenseNumber ?? d.license ?? d.License,
        note:
            d.note ??
            d.Note ??
            d.notes ??
            d.Notes ??
            d.exemptionOverrideNote ??
            d.ExemptionOverrideNote ??
            d.manualTaxCodeNote ??
            d.ManualTaxCodeNote ??
            d.overrideNote ??
            d.OverrideNote ??
            d.comments ??
            d.Comments ??
            // Some APIs return the note as an audit log entry: { log: { entry } }
            noteFromLog,
        signedDate:
            d.signedDate ??
            d.SignedDate ??
            d.effectiveDate ??
            d.EffectiveDate ??
            d.startDate ??
            d.StartDate,
        expirationDate: d.expirationDate ?? d.ExpirationDate ?? d.endDate ?? d.EndDate,
        isSST:
            d.isSST ??
            d.IsSST ??
            (zone as Record<string, unknown> | undefined)?.isSst ??
            (zone as Record<string, unknown> | undefined)?.IsSst ??
            (regionObj as Record<string, unknown> | undefined)?.isSst ??
            (regionObj as Record<string, unknown> | undefined)?.IsSst,
        initials:
            d.initials ??
            d.Initials ??
            (zone as Record<string, unknown> | undefined)?.initials ??
            (regionObj as Record<string, unknown> | undefined)?.initials,
        manualTaxCodeChangesId: d.manualTaxCodeChangesId ?? d.ManualTaxCodeChangesId,
        exemptionOverrideId: d.exemptionOverrideId ?? d.ExemptionOverrideId ?? d.id,
        ...(apiActive !== undefined ? { exemptionOverrideIsActive: apiActive } : {}),
    };
}

/**
 * When GET …/exemption-overrides is missing, blocked, or ASP.NET returns ambiguous route (500), callers fall back to exemptions-details.
 */
function shouldFallBackExemptionOverridesListFromError(error: unknown): boolean {
    const ax = error as AxiosError<{ title?: string; detail?: string }>;
    const status = ax?.response?.status;
    if (status === 404 || status === 405) {
        return true;
    }
    if (status !== 500) {
        return false;
    }
    const data = ax?.response?.data;
    const title = String(data?.title ?? "");
    const detail = String(data?.detail ?? "");
    return (
        /AmbiguousMatchException/i.test(title) ||
        /AmbiguousMatchException/i.test(detail) ||
        /matched multiple endpoints/i.test(detail)
    );
}

/**
 * Lists exemption overrides for a customer (`GET …/customers/{id}/exemption-overrides`).
 * Returns `null` if the collection endpoint is not available (404/405) or unusable (e.g. ASP.NET
 * AmbiguousMatchException when two controller actions share the same route) so the caller can fall back to exemptions-details.
 */
export async function fetchCustomerExemptionOverridesList(
    customerId: number
): Promise<Array<ExemptionsDetailsRow> | null> {
    try {
        const res = await axios.get(
            buildApiV3Url(`customers/${customerId}/exemption-overrides?$top=500`),
            { withCredentials: true }
        );
        const body: unknown = res?.data;
        const raw: unknown =
            body !== null &&
            typeof body === "object" &&
            "value" in body &&
            Array.isArray((body as { value: unknown }).value)
                ? (body as { value: unknown[] }).value
                : body;
        if (!Array.isArray(raw)) {
            return [];
        }
        return raw.map((item: unknown) =>
            normalizeExemptionsDetailsRow({
                ...mapExemptionOverrideApiDetailToGridRowShape(item),
            })
        );
    } catch (e) {
        if (shouldFallBackExemptionOverridesListFromError(e)) {
            return null;
        }
        throw e;
    }
}

/**
 * Updates an existing customer exemption override (same body as POST).
 * @param recordId — {@link getExemptionOverrideRecordId}
 */
export function putCustomerExemptionOverride(
    customerId: number,
    recordId: number,
    payload: PostExemptionOverridePayload
) {
    return axios.put(
        buildApiV3Url(`customers/${customerId}/exemption-overrides/${recordId}`),
        payload,
        { withCredentials: true }
    );
}

/**
 * Deletes a customer exemption override.
 * @param recordId — {@link getExemptionOverrideRecordId}
 */
export function deleteCustomerExemptionOverride(customerId: number, recordId: number) {
    return axios.delete(buildApiV3Url(`customers/${customerId}/exemption-overrides/${recordId}`), {
        withCredentials: true,
    });
}
